import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Trophy, Coins } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { createClient } from '../lib/supabase';

interface PlayerRanking {
  username: string;
  displayUsername: string;
  chips: number;
}

export default function ClassificacioPage() {
  const [rankings, setRankings] = useState<PlayerRanking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('profiles')
          .select('username, chips')
          .order('chips', { ascending: false });

        if (error) {
          console.error('Error fetching rankings:', error);
          return;
        }

        if (data) {
          const formatted = data.map((player) => {
            // Ocultar les 4 últimes xifres del nom d'usuari (PIN)
            const username = player.username || '';
            let displayUsername = username;
            
            if (username.length > 4) {
              // Mostrar només la part del nom sense el PIN (4 últimes xifres)
              displayUsername = username.slice(0, -4);
            } else {
              // Si el nom d'usuari té 4 o menys caràcters, mostrar buit
              displayUsername = '';
            }
            
            // Mostrar les fitxes completament amb format de milers
            const chips = player.chips || 0;
            
            return {
              username: player.username,
              displayUsername: displayUsername,
              chips: chips,
            };
          });

          setRankings(formatted);
        }
      } catch (error) {
        console.error('Error fetching rankings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, []);

  return (
    <div className="min-h-screen bg-black overflow-y-auto">
      <Navbar />
      <div className="pt-24 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
              Classificació
            </h1>
            <p className="text-zinc-400 text-sm sm:text-base">
              Rànquing de jugadors ordenat per fitxes
            </p>
          </div>

          <Card className="border-zinc-800 bg-zinc-900/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-cyan-400" />
                Rànquing
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center text-zinc-400 py-8">
                  Carregant classificació...
                </div>
              ) : rankings.length === 0 ? (
                <div className="text-center text-zinc-400 py-8">
                  No hi ha jugadors disponibles
                </div>
              ) : (
                <div className="space-y-2">
                  {rankings.map((player, index) => (
                    <div
                      key={player.username}
                      className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-700 text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-white font-medium">{player.displayUsername}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Coins className="w-4 h-4 text-amber-400" />
                        <span className="text-white font-semibold">{player.chips.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
